import React, { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import inviteStyles from '../../_css/Invite.module.css'
import { Card, CardTitle, TableHeader, TableCell, Badge } from '../UI'
import UpdatePasswordModal from '../Modals/UpdatePasswordModal'
import { updatePassword } from '../../requests'

const PER_PAGE = 5

const Admins = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const adminsList = location?.state?.admins ?? []
  const admins = Array.isArray(adminsList) ? adminsList : []
  const agencyData = location?.state?.agencyData ?? null

  const [currentPage, setCurrentPage] = useState(1)
  const [passwordModalOpen, setPasswordModalOpen] = useState(false)
  const [selectedAdmin, setSelectedAdmin] = useState(null)

  const totalItems = admins.length
  const totalPages = Math.max(1, Math.ceil(totalItems / PER_PAGE))
  const startIndex = (currentPage - 1) * PER_PAGE
  const endIndex = startIndex + PER_PAGE

  const paginatedAdmins = useMemo(
    () => admins.slice(startIndex, endIndex),
    [admins, startIndex, endIndex]
  )

  const handlePageChange = (page) => {
    const next = Math.max(1, Math.min(page, totalPages))
    setCurrentPage(next)
  }

  const handleBack = () => {
    const state = location?.state
    if (state?.returnToAgencyModal && state?.agencyData) {
      navigate('/agencies', {
        state: {
          openViewAgencyModal: true,
          agencyData: state.agencyData,
          agencyStats: state.agencyStats ?? null,
          admins: state.admins ?? [],
        },
      })
    } else {
      navigate(-1)
    }
  }

  const openPasswordModal = (admin) => {
    setSelectedAdmin(admin)
    setPasswordModalOpen(true)
  }

  const closePasswordModal = () => {
    setPasswordModalOpen(false)
    setSelectedAdmin(null)
  }

  return (
    <>
      <div className={inviteStyles.page}>
        <div className={inviteStyles.headerRow}>
          <div className={inviteStyles.titleBlock}>
            <h1>{agencyData?.name ?? 'Agency admins'}</h1>
            <p>View all agency admins</p>
          </div>
          <button
            type="button"
            className={inviteStyles.secondaryBtn}
            onClick={handleBack}
          >
            ← Back to Agency
          </button>
        </div>

        <Card>
          <CardTitle>All admins</CardTitle>
          <div className={inviteStyles.tableWrap}>
            <table className={inviteStyles.table}>
              <thead>
                <TableHeader columns={['Name', 'Email', 'Status', 'Actions']} />
              </thead>
              <tbody>
                {paginatedAdmins.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ padding: 0, border: 'none' }}>
                      <p className={inviteStyles.emptyText}>No admins found</p>
                    </td>
                  </tr>
                ) : (
                  paginatedAdmins.map((admin) => (
                    <tr key={admin.id}>
                      <TableCell bold>
                        {admin.full_name ?? admin.fullName ?? '—'}
                      </TableCell>
                      <TableCell>{admin.email ?? '—'}</TableCell>
                      <TableCell>
                        <Badge color={admin.isActive ? '#22c55e' : '#6b7280'}>
                          {admin.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          className={inviteStyles.secondaryBtn}
                          style={{ padding: '6px 12px', fontSize: 12 }}
                          onClick={() => openPasswordModal(admin)}
                        >
                          Update password
                        </button>
                      </TableCell>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className={inviteStyles.pagination}>
            <p className={inviteStyles.paginationInfo}>
              Showing {totalItems === 0 ? 0 : startIndex + 1}–
              {Math.min(endIndex, totalItems)} of {totalItems} admins
              {totalPages > 1 && (
                <>
                  {' '}
                  · Page {currentPage} of {totalPages}
                </>
              )}
            </p>
            {totalPages > 1 && (
              <div className={inviteStyles.paginationBtns}>
                <button
                  type="button"
                  className={inviteStyles.pageBtn}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  title="Previous page"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  type="button"
                  className={inviteStyles.pageBtn}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  title="Next page"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        </Card>
      </div>

      <UpdatePasswordModal
        open={passwordModalOpen}
        admin={selectedAdmin}
        onClose={closePasswordModal}
        onSubmit={updatePassword}
      />
    </>
  )
}

export default Admins
